// react_frontend/src/Components/Modules/Category/EditCategory.js
import React, { useState, useEffect, useRef, useContext  } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { MotherContext } from "../../../Context/MotherContext";
// RSuite Components
import {
  Form,
  Button,
  Message,
  Loader,
  Whisper,
  Tooltip,
  Schema,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaTags,
  FaLink,
  FaQrcode,
  FaClipboardList,
  FaPencilAlt,
} from "react-icons/fa";

const EditCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateCategoryInList } = useContext(MotherContext);
  const [formValue, setFormValue] = useState({});
  const [formError, setFormError] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef();

  // Validation Schema
  const { StringType, NumberType } = Schema.Types;

  const model = Schema.Model({
    category_name: StringType().isRequired("Category name is required."),
    category_slug: StringType().isRequired("Category slug is required."),
    category_code: NumberType()
      .min(1, "Category code must be a positive number.")
      .isRequired("Category code is required."),
  });

  // Fetch Category Data
  useEffect(() => {
    const fetchCategory = async () => {
      setLoading(true);
      try {
        const response = await AxiosAuthInstance.get(`add_category/${id}`);
        const cat = response.data.category;

        setFormValue({
          category_name: cat.category_name,
          category_slug: cat.category_slug,
          category_code: cat.category_code,
        });
      } catch (error) {
        console.error("Failed to fetch category:", error);
        toaster.push(
          <Message type="error" duration={5000}>
            Failed to load category data.
          </Message>,
          { placement: "topEnd" }
        );
        navigate("/category");
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [id, navigate]);

  // Auto-generate slug when category name changes
  const handleNameChange = (value) => {
    const slug = value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    setFormValue({
      ...formValue,
      category_name: value,
      category_slug: slug,
    });
  };

  // Handle Submit
  const handleSubmit = async () => {
    if (!formRef.current.check()) return;

    setLoading(true);
    try {
      const res = await AxiosAuthInstance.put(`add_category/${id}`, formValue);

      if (res.data.status === true) {
        toaster.push(
          <Message type="success" duration={3000}>
            Category updated successfully!
          </Message>,
          { placement: "topEnd" }
        );
        updateCategoryInList({ ...formValue, id });
        navigate("/category");
      }
    } catch (err) {
      const serverErrors = err.response?.data?.errors || {};
      const formattedErrors = Object.keys(serverErrors).reduce((acc, key) => {
        acc[key] = serverErrors[key][0];
        return acc;
      }, {});
      setFormError(formattedErrors);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/category");
  };

  return (
    <>
      <Breadcrumb title="Edit Category" />

      <div className="p-3">
        <div
          className="card shadow-sm border-0 mx-auto"
          style={{ maxWidth: "700px" }}
        >
          {/* Header */}
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ backgroundColor: "#4351A3", color: "white" }}
          >
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <FaPencilAlt /> Edit Category
            </h5>
            <Link to="/category" className="text-white text-decoration-none">
              <Whisper
                placement="left"
                speaker={<Tooltip>Category List</Tooltip>}
              >
                <Button size="sm" appearance="subtle" className="text-white">
                  <FaClipboardList /> List
                </Button>
              </Whisper>
            </Link>
          </div>

          {/* Body */}
          <div className="card-body bg-light p-4">
            <Form
              ref={formRef}
              fluid
              formValue={formValue}
              formError={formError}
              onChange={setFormValue}
              onCheck={setFormError}
              model={model}
            >
              {/* Category Name */}
              <Form.Group controlId="category_name">
                <Form.ControlLabel>Category Name *</Form.ControlLabel>
                <Form.Control
                  name="category_name"
                  placeholder="e.g., Electronics"
                  prefix={<FaTags />}
                  onChange={handleNameChange}
                />
                <Form.HelpText className="text-muted">
                  Slug will be auto-generated from name.
                </Form.HelpText>
                <Form.ErrorMessage name="category_name" />
              </Form.Group>

              {/* Category Slug */}
              <Form.Group controlId="category_slug">
                <Form.ControlLabel>Category Slug *</Form.ControlLabel>
                <Form.Control
                  name="category_slug"
                  placeholder="electronics"
                  prefix={<FaLink />}
                  readOnly
                  style={{ backgroundColor: "#f8f9fa", fontStyle: "italic" }}
                />
                <Form.HelpText className="text-muted">
                  Auto-generated. Edit name to change slug.
                </Form.HelpText>
                <Form.ErrorMessage name="category_slug" />
              </Form.Group>

              {/* Category Code */}
              <Form.Group controlId="category_code">
                <Form.ControlLabel>Category Code *</Form.ControlLabel>
                <Form.Control
                  name="category_code"
                  type="number"
                  min="1"
                  placeholder="e.g., 101"
                  readOnly
                  prefix={<FaQrcode />}
                />
                <Form.HelpText className="text-muted">
                                  Auto-generated.
                                </Form.HelpText>
                <Form.ErrorMessage name="category_code" />
              </Form.Group>

              {/* Action Buttons */}
              <div
                className="d-flex gap-3 pt-3 border-top"
                style={{ marginTop: "2rem" }}
              >
                <Button
                  appearance="primary"
                  color="blue"
                  size="md"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? (
                    <>
                      <Loader size="sm" /> Updating...
                    </>
                  ) : (
                    "Update Category"
                  )}
                </Button>

                <Button
                  className="border"
                  appearance="subtle"
                  color="red"
                  size="md"
                  onClick={handleCancel}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditCategory;
